import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { ClientService } from '@lib/client/client.service';
import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentsModule, TabAppearance, TabItem, SnackBarService, DialogService } from '@mastechnicaegroup/carelinelive-ui';
import { ClientGeneralInformationComponent } from './accordion/general-information/client-general-information.component';
import { ClientContactAddressComponent } from './accordion/contact-address/client-contact-address.component';
import { ClientAdditionalCommentsComponent } from './accordion/additional-comments/client-additional-comments.component';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, map, of, switchMap, take, tap } from 'rxjs';
import { RefreshableResult, successfulResult, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EditClientAdditionalCommentsDialogComponent } from '../../dialogs/client/edit/additional-comments/edit-client-additional-comments-dialog.component';
import { EditClientContactAddressDialogComponent } from '../../dialogs/client/edit/contact-address/edit-client-contact-address-dialog.component';
import { EditClientGeneralInformationDialogComponent } from '../../dialogs/client/edit/general-information/edit-client-general-information-dialog.component';
import { getDisplayName } from '@lib/api/models/person';
import { ClientPackagesTabComponent } from './tabs/packages/client-packages-tab.component';
import { ClientAuditTabComponent } from './tabs/audit/client-audit-tab.component';
import { Client } from '@lib/api/models/client';
import { PromptClientStatusChangeDialogComponent } from '../../dialogs/client/prompt/prompt-client-status-change-dialog.component';
import { ClientDocumentsTabComponent } from './tabs/documents/client-documents-tab.component';

@Component({
    selector: 'app-client-view-page',
    templateUrl: './client-view-page.component.html',
    styleUrl: './client-view-page.component.scss',
    standalone: true,
    imports: [
        ClientContactAddressComponent,
        ClientAdditionalCommentsComponent,
        ClientGeneralInformationComponent,
        CommonModule,
        ComponentsModule,
        ErrorPanelComponent,
        FormsModule,
        HeaderComponent,
        ReactiveFormsModule,
        ClientPackagesTabComponent,
        ClientAuditTabComponent,
        ClientDocumentsTabComponent
    ]
})

export class ClientViewPageComponent {

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _clientService = inject(ClientService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _dialog = inject(DialogService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _authService = inject(AuthenticationService);

    protected readonly getDisplayName = getDisplayName;

    protected currentUser = toSignal(this._authService.user$);

    protected readonly auth = inject(AuthenticationService);

    protected currentTabId = '0';
    protected isPackagesTabOpen = true;
    protected isDocumentsTabOpen = false;
    protected isAuditTabOpen = false;

    tabAppearance: TabAppearance = 'contained';
    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '0',
            title: 'Packages'
        },
        {
            disabled: false,
            id: '1',
            title: 'Audit'
        },
    ];

    client = new RefreshableResult({
        source: this._activatedRoute.params.pipe(
            map(({ id }) => id),
            switchMap(id => this._clientService.getClient(id).pipe(
                toResult()
            ))
        )
    });

    client$ = this.client.result$.pipe(
        successfulResult()
    );

    editGeneralInformation() {
        this.client$.pipe(
            take(1),
            switchMap((clientResult) => {
                return EditClientGeneralInformationDialogComponent.show(this._dialog, clientResult.data).closed.pipe(
                    tap(() => {
                        this.client.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Couldn\'t add General Information.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    editContactAddressDetails() {
        this.client$.pipe(
            take(1),
            switchMap((clientResult) => {
                return EditClientContactAddressDialogComponent.show(this._dialog, clientResult.data).closed.pipe(
                    tap(() => {
                        this.client.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to add contact/address details.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    editAdditionalComments() {
        this.client$.pipe(
            take(1),
            switchMap((clientResult) => {
                return EditClientAdditionalCommentsDialogComponent.show(this._dialog, clientResult.data).closed.pipe(
                    tap(() => {
                        this.client.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to add additional comments.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    openPackagesTab() {
        this.closeAllTabs();
        this.isPackagesTabOpen = true;
    }

    openDocumentsTab() {
        this.closeAllTabs();
        this.isDocumentsTabOpen = true;
    }

    openAuditTab() {
        this.closeAllTabs();
        this.isAuditTabOpen = true;
    }

    closeAllTabs() {
        this.isPackagesTabOpen = false;
        this.isDocumentsTabOpen = false;
        this.isAuditTabOpen = false;
    }

    changeStatus(client: Client, status: boolean) {

        const clientData = {
            ...client,
            status: status
        };

        return PromptClientStatusChangeDialogComponent.show(this._dialog, clientData).closed.pipe(
            tap(() => {
                this.client.refresh();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Client status failed to change',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }

    public currentTabIdChange(tabId: string) {
        this.currentTabId = tabId;
        switch (tabId) {
            case '0':
                this.openPackagesTab();
                break;
            case '1':
                this.openAuditTab();
                break;
        }
    }
}
